import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import classNames from 'classnames'
import {memo, useEffect} from 'react'
import {graphql, useFragment} from 'react-relay'
import {useLocation} from 'react-router-dom'

import {useThrottle} from '../../../../../hooks/useThrottle'
import {getBuild} from '../../../../../selectors'
import {restApi} from '../../../../../services/rest'
import {stringifyId} from '../../../../../types'
import type {BuildId} from '../../../../../types'
import Grid from '../../../../common/Grid/Grid'
import Pager from '../../../../common/Pager/Pager.container'
import {usePage} from '../../../../common/Pager/Pager.hooks'
import {PagerGroup} from '../../../../common/Pager/Pager.types'
import TestScopePagerWarning from '../../../../common/Pager/TestScopePager/TestScopePagerWarning'
import {getTestScopesArg} from '../../../../packages/Tests/Tests.rest'
import {OrderByField} from '../../../../packages/Tests/Tests.types'
import {defaultFilterTemplate, getFilterQueryParams, getTestsLocator} from '../BuildTestsTab.utils'
import {
  getStatusQueryParam,
  TestFilterStatus,
} from '../TestsFilter/TestsStatusFilter/TestsStatusFilter.utils'
import {ViewType} from '../TestsFilter/TestsViewSelect/TestsViewSelect.utils'

import type {TestScopesViewCountsFragment$key} from './__generated__/TestScopesViewCountsFragment.graphql'
import TestScope from './TestScope/TestScope'
import {getScopeKeyByViewType, getScopesLocator} from './TestScopesView.utils'
import TestScopesViewHeader from './TestScopesViewHeader/TestScopesViewHeader'

import styles from './TestScopesView.css'

import {useAppSelector} from 'src/hooks/react-redux'

const countsFragment = graphql`
  fragment TestScopesViewCountsFragment on TestCounters {
    success
    failed
    muted
    ignored
  }
`

type Props = {
  buildId: BuildId
  countsKey: TestScopesViewCountsFragment$key | null
}
const COUNT_VISIBLE_SCOPES = 100
const THROTTLER_TIMEOUT = 5000

function TestScopesView({buildId, countsKey}: Props) {
  const location = useLocation()

  const filterQueryParams = getFilterQueryParams(location, {
    ...defaultFilterTemplate,
    view: ViewType.SUITES,
    orderBy: OrderByField.DURATION,
  })
  const scopeKey = getScopeKeyByViewType(filterQueryParams.view)
  const counts = useFragment(countsFragment, countsKey)
  const success = counts?.success ?? 0
  const failed = counts?.failed ?? 0
  const muted = counts?.muted ?? 0
  const ignored = counts?.ignored ?? 0
  const status = getStatusQueryParam(location)

  const testOccurrencesLocator = getTestsLocator({
    onlyNew: filterQueryParams.onlyNew,
    name: filterQueryParams.name,
    withoutInvestigator: filterQueryParams.withoutInvestigator,
    status,
    count: -1,
    baseLocator: `build:(id:${stringifyId(buildId)})`,
  })
  const locator = getScopesLocator({
    testOccurrencesLocator,
    order: filterQueryParams.order,
    orderBy: filterQueryParams.orderBy,
    scope: filterQueryParams.scope,
    count: COUNT_VISIBLE_SCOPES,
    currentPage: usePage(),
  })
  const count = useAppSelector(state => getBuild(state, buildId)?.testOccurrences?.count)

  const {data, isReady, isLoadingNewLocator, refetch} = restApi.endpoints.getTestScopes.useQuery(
    getTestScopesArg(scopeKey, locator, COUNT_VISIBLE_SCOPES),
    {
      selectFromResult: ({data, currentData}) => ({
        data: data?.testScope,
        isReady: data != null,
        isLoadingNewLocator: data !== currentData,
      }),
    },
  )
  const fetchTestScopesWithThrottle = useThrottle(refetch, THROTTLER_TIMEOUT)

  useEffect(() => {
    fetchTestScopesWithThrottle()
  }, [count, fetchTestScopesWithThrottle])

  const withCountersColumns = status === TestFilterStatus.ALL

  return isReady ? (
    <>
      <Grid
        className={classNames(styles.grid, {
          [styles.withSuccessColumn]: success > 0,
          [styles.withFailureColumn]: failed > 0,
          [styles.withMutedColumn]: muted > 0,
          [styles.withIgnoredColumn]: ignored > 0,
          [styles.withCountersColumns]: withCountersColumns,
          [styles.withOverlay]: isLoadingNewLocator,
        })}
      >
        <TestScopesViewHeader withCountersColumns={withCountersColumns} />
        {data?.map(scope => (
          <TestScope
            key={[scope.suite, scope.package, scope.class].join('/')}
            scopeKey={scopeKey}
            scope={scope}
            withCountersColumns={withCountersColumns}
          />
        ))}
      </Grid>
      <Pager
        group={PagerGroup.TEST_SCOPE}
        warningComponent={TestScopePagerWarning}
        isLoading={isLoadingNewLocator}
      />
    </>
  ) : (
    <LoaderInline>{'Loading...'}</LoaderInline>
  )
}

export default memo<Props>(TestScopesView)
